import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdvanceWorkflowDto,
  Bilingual,
  FileDocument,
  FileWorkflowState,
  LocalGovernmentWorkflows,
  SaveWorkflowStepDto,
  WorkflowDocumentLocale,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { apiErrorStatus, useMutationToast } from './mutation-toast';
import { deadlinesKeys } from './deadlines-keys';
import { documentKeys } from './documents-api';
import { filesKeys } from './files-api';

/**
 * By-law workflows: which by-law types a municipality runs, and the configured
 * workflow of one file. Every definition arrives already resolved against the
 * municipality's jurisdiction, so a screen only ever renders data.
 */
export const bylawWorkflowKeys = {
  localGovernment: (localGovernmentId: string) =>
    ['bylaw-workflows', 'lg', localGovernmentId] as const,
  file: (fileId: string) => ['bylaw-workflows', 'file', fileId] as const,
};

/** Pick one language out of a bilingual data string. */
export function pickLang(text: Bilingual | undefined, language: string | undefined): string {
  if (!text) return '';
  return language?.startsWith('fr') ? text.fr : text.en;
}

export function useLocalGovernmentWorkflows(localGovernmentId: string | null | undefined) {
  return useQuery({
    queryKey: bylawWorkflowKeys.localGovernment(localGovernmentId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<LocalGovernmentWorkflows>(
        `/local-governments/${localGovernmentId}/bylaw-workflows`,
      );
      return data;
    },
    enabled: !!localGovernmentId,
    staleTime: 5 * 60_000,
    // A municipality the caller cannot read the workflows of simply offers the
    // original categories; there is nothing to retry.
    retry: false,
  });
}

export function useUpdateCitations(localGovernmentId: string) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (citations: Record<string, string>) => {
      const { data } = await api.put<LocalGovernmentWorkflows>(
        `/local-governments/${localGovernmentId}/bylaw-workflows/citations`,
        { citations },
      );
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(bylawWorkflowKeys.localGovernment(localGovernmentId), data);
      toast.success('gov.bylawWorkflow.admin.saved');
    },
    onError: (err) => toast.error(err, 'gov.bylawWorkflow.admin.saveError'),
  });
}

export function useFileWorkflow(fileId: string, enabled = true) {
  return useQuery({
    queryKey: bylawWorkflowKeys.file(fileId),
    queryFn: async () => {
      const { data } = await api.get<FileWorkflowState>(`/files/${fileId}/bylaw-workflow`);
      return data;
    },
    enabled: enabled && !!fileId,
  });
}

/** Everything a workflow write can move: the file itself, its clocks, its state. */
function useAfterWorkflowWrite(fileId: string) {
  const qc = useQueryClient();
  return {
    applied(state: FileWorkflowState) {
      qc.setQueryData(bylawWorkflowKeys.file(fileId), state);
      void qc.invalidateQueries({ queryKey: filesKeys.detail(fileId) });
      void qc.invalidateQueries({ queryKey: filesKeys.all });
      void qc.invalidateQueries({ queryKey: deadlinesKeys.all });
    },
    failed(err: unknown) {
      // The file moved on somewhere else; show where it is now.
      if (apiErrorStatus(err) === 409) {
        void qc.invalidateQueries({ queryKey: bylawWorkflowKeys.file(fileId) });
        void qc.invalidateQueries({ queryKey: filesKeys.detail(fileId) });
      }
    },
  };
}

export function useSaveWorkflowStep(fileId: string) {
  const after = useAfterWorkflowWrite(fileId);
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async ({ stepKey, dto }: { stepKey: string; dto: SaveWorkflowStepDto }) => {
      const { data } = await api.put<FileWorkflowState>(
        `/files/${fileId}/bylaw-workflow/steps/${stepKey}`,
        dto,
      );
      return data;
    },
    onSuccess: (state) => {
      after.applied(state);
      toast.success('gov.bylawWorkflow.stage.saved');
    },
    onError: (err) => {
      after.failed(err);
      toast.error(err, 'gov.bylawWorkflow.stage.saveError');
    },
  });
}

export function useAdvanceWorkflow(fileId: string) {
  const after = useAfterWorkflowWrite(fileId);
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (dto: AdvanceWorkflowDto) => {
      const { data } = await api.post<FileWorkflowState>(
        `/files/${fileId}/bylaw-workflow/advance`,
        dto,
      );
      return data;
    },
    onSuccess: (state) => {
      after.applied(state);
      toast.success(
        state.stage === 'closed'
          ? 'gov.bylawWorkflow.stage.closed'
          : 'gov.bylawWorkflow.stage.advanced',
      );
    },
    onError: (err) => {
      after.failed(err);
      toast.error(err, 'gov.bylawWorkflow.stage.advanceError');
    },
  });
}

export function useGenerateWorkflowDocument(fileId: string) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async ({
      templateKey,
      locale,
    }: {
      templateKey: string;
      locale: WorkflowDocumentLocale;
    }) => {
      const { data } = await api.post<FileDocument>(
        `/files/${fileId}/bylaw-workflow/documents/${templateKey}`,
        { locale },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: documentKeys.forFile(fileId) });
      toast.success('gov.bylawWorkflow.documents.generated');
    },
    onError: (err) => toast.error(err, 'gov.bylawWorkflow.documents.generateError'),
  });
}
